import { useEffect, useRef } from 'react'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import { useKey } from 'react-use'
import type { UseMutationProps } from '@ztechapps/common.hook'
import { useInlineEditor, useMutation } from '@ztechapps/common.hook'
import type { InlineInputUpdateParamsFunction, InlineInputValidateFunction } from '@ztechapps/webui.input'
import { useAddressEditPopOverContext } from './Context'

export type UseAddressEditPopOverProps = {
  editorKey: string
  name: string
  initialValues?: Record<string, any>
  mutationConfig: UseMutationProps
  updateParams?: InlineInputUpdateParamsFunction
  validate?: InlineInputValidateFunction
}

export type UseAddressEditPopOverResult = {
  value: any
  setNewValue: (value: any) => void
  /** Resolves `true` only on a genuine successful save — `useInlineEditor.saveNow` never rejects
   * (see `saveNowCore` below), so `succeededRef` is the only honest signal. */
  saveNow: () => Promise<boolean>
  cancelNow: () => void
  submitting: boolean
  error: string
}

// Composes the shared `useMutation` and `useInlineEditor` exactly as `InlineInput` does
// (libs/webui/input/src/InlineInput/index.tsx) to get `setNewValue`, `saveNow`, `cancelNow`,
// `saving`, `error`. Neither hook is modified; the `editable` and `onClick` they return are
// ignored because open-state lives in `AddressEditPopOverContext` instead.
export const useAddressEditPopOver = ({
  editorKey,
  name,
  initialValues,
  mutationConfig,
  updateParams,
  validate,
}: UseAddressEditPopOverProps): UseAddressEditPopOverResult => {
  const { openKey, close: closeContext } = useAddressEditPopOverContext()
  const isOpen = openKey === editorKey
  const [mutationFn] = useMutation(mutationConfig)
  const inputRef = useRef(null)
  const succeededRef = useRef(false)

  const initialValue = get(initialValues, name)

  const update = async (updatedValue: any) => {
    // Mirror `InlineInput`'s own unchanged-value short-circuit (index.tsx) — Save with no edits
    // should not fire a write mutation on either arm.
    // eslint-disable-next-line eqeqeq
    if (updatedValue == initialValue) {
      succeededRef.current = true
      return
    }

    succeededRef.current = false

    // Run validation (when available) and let it throw. `InlineInput`'s own `update`
    // (libs/webui/input/src/InlineInput/index.tsx) runs the identical validate-then-mutate shape,
    // but does not itself throw on a mutation error — this hook does, so the identical
    // `useInlineEditor` error handling ends up surfacing it.
    if (validate) {
      await validate(updatedValue)
    }

    const updateParamsResult = updateParams
      ? updateParams({ _id: initialValues?._id, updatedValue, name, initialValues })
      : { input: { _id: initialValues?._id, [name]: updatedValue } }

    const result = (await mutationFn(updateParamsResult)) ?? {}
    const errors = (result as { errors?: any })?.errors
    if (!isEmpty(errors)) {
      // Throw the original errors, not a flattened string — `new Error(errors)` stringifies the
      // array (`"Error: [object Object]"`) and loses the structured `extensions.exception.
      // validationErrors` shape `useError.onError` (libs/common/hook/src/useError/index.ts) reads
      // off `graphQLErrors`. Mimics that same Apollo `ApolloError` shape on a real `Error`.
      const mutationError = new Error(errors[0]?.message ?? 'Something went wrong.')
      Object.assign(mutationError, { graphQLErrors: errors })
      throw mutationError
    }

    succeededRef.current = true
  }

  const {
    setNewValue,
    newValue,
    saveNow: saveNowCore,
    cancelNow: cancelNowCore,
    saving,
    error,
  } = useInlineEditor(initialValue, update, inputRef)

  // Escape must run the identical path as Cancel: discard the in-progress edit and close. Guarded
  // by `isOpen` — `useKey` binds to `window`, so every mounted surface's hook instance receives the
  // keydown; only the surface that owns the open card may act on it.
  const close = () => {
    cancelNowCore()
    closeContext()
  }

  // Gated on `!event.defaultPrevented`: the card holds three `react-select` instances (the
  // picker, `CountrySelect`, `UsStateSelect`), and react-select's own Escape handling closes its
  // menu and calls `preventDefault()` but never `stopPropagation()` — the bubbled keydown still
  // reaches this `window` listener. Without the guard, dismissing a dropdown's menu also discards
  // the whole card. The accepted consequence: a first Escape closes the dropdown (react-select
  // still calls `preventDefault` even with its menu already closed), a second Escape closes the
  // card — the standard nested-overlay contract, not silent data loss.
  useKey(
    'Escape',
    (event) => {
      if (isOpen && !event.defaultPrevented) close()
    },
    undefined,
    [isOpen]
  )

  // The requirement is that opening any editor closes any other, discarding its in-progress edit.
  // `Context` only swaps `openKey`; this surface stays mounted, so without this its `newValue`
  // would survive the swap and reappear on reopen. Run the same discard Cancel and Escape already
  // run whenever this surface stops being the open one — including when a save on THIS surface
  // closes it, where discarding back to `initialValue` is a no-op once the save's data lands.
  const wasOpenRef = useRef(isOpen)
  useEffect(() => {
    if (wasOpenRef.current && !isOpen) {
      cancelNowCore()
    }
    wasOpenRef.current = isOpen
  }, [isOpen, cancelNowCore])

  const saveNow = async () => {
    await saveNowCore()
    const succeeded = succeededRef.current
    // Close on a successful save; leave the popover open on a failed one, where `error` already
    // surfaces via `useInlineEditor`'s own toast plus whatever the caller renders from the
    // returned `error` (`AddressPopoverEditField` feeds it into `InlineDisplayContent`'s `meta`,
    // matching `InlineInput`'s own maroon-label-plus-helper-text rendering).
    if (succeeded) {
      closeContext()
    }
    return succeeded
  }

  return { value: newValue, setNewValue, saveNow, cancelNow: close, submitting: saving, error }
}
